/**
 * Cmarkdownxychart — responsive layout engine
 *
 * Builds a fully laid-out chart from (parsed data, container width, container
 * height, zoom factor). The chart spreads to fill the container; zoom > 1
 * stretches the category axis so the chart becomes scrollable.
 */

import {
  XYCHART_BAR_GAP,
  XYCHART_BAR_GROUP_RATIO,
  XYCHART_BAR_RADIUS,
  XYCHART_HORIZONTAL_MARGINS,
  XYCHART_MIN_HEIGHT,
  XYCHART_MIN_WIDTH,
  XYCHART_SERIES_COLORS,
  XYCHART_VERTICAL_MARGINS,
} from './xychart-constants';
import {
  XYCHART_SERIES_TYPE,
  type XYChartDataSeries,
  type XYChartBarDatum,
  type XYChartCategoryLabel,
  type XYChartLayout,
  type XYChartLinePoint,
  type XYChartMargins,
  type XYChartParsed,
  type XYChartPoint,
  type XYChartSeriesLayout,
  type XYChartTickLayout,
} from './xychart-types';

const TARGET_TICK_COUNT = 5;
const HORIZONTAL_CATEGORY_LABEL_MAX_LENGTH = 12;
const VERTICAL_CATEGORY_LABEL_MAX_LENGTH = 10;
const ROTATED_CATEGORY_LABEL_MIN_COUNT = 8;
const ROTATED_CATEGORY_LABEL_STEP_LIMIT = 56;
const NICE_NUMBER_BASE = 10;
const VALUE_DOMAIN_PADDING_RATIO = 0.1;
const EQUAL_DOMAIN_SPREAD_FALLBACK = 1;
const EQUAL_TICK_SPREAD_RATIO = 0.2;
const NICE_MULTIPLIER_FALLBACK = 10;

interface NiceMultiplierRule {
  limit: number;
  multiplier: number;
}

const NICE_MULTIPLIER_MODE = {
  Rounded: 'rounded',
  Ceiling: 'ceiling',
} as const;

type NiceMultiplierMode = (typeof NICE_MULTIPLIER_MODE)[keyof typeof NICE_MULTIPLIER_MODE];

const NICE_MULTIPLIER_RULES_BY_MODE: Record<NiceMultiplierMode, NiceMultiplierRule[]> = {
  [NICE_MULTIPLIER_MODE.Rounded]: [
    { limit: 1.5, multiplier: 1 },
    { limit: 3, multiplier: 2 },
    { limit: 7, multiplier: 5 },
  ],
  [NICE_MULTIPLIER_MODE.Ceiling]: [
    { limit: 1, multiplier: 1 },
    { limit: 2, multiplier: 2 },
    { limit: 5, multiplier: 5 },
  ],
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function scaleLinear(value: number, domain: [number, number], range: [number, number]): number {
  if (domain[0] === domain[1]) {
    return (range[0] + range[1]) / 2;
  }
  const ratio = (value - domain[0]) / (domain[1] - domain[0]);
  return range[0] + ratio * (range[1] - range[0]);
}

function selectNiceMultiplier(input: {
  fraction: number;
  round: boolean;
}): number {
  const mode = input.round
    ? NICE_MULTIPLIER_MODE.Rounded
    : NICE_MULTIPLIER_MODE.Ceiling;
  const matchingRule = NICE_MULTIPLIER_RULES_BY_MODE[mode].find(
    (rule) => input.fraction < rule.limit || (!input.round && input.fraction === rule.limit),
  );

  return matchingRule?.multiplier ?? NICE_MULTIPLIER_FALLBACK;
}

function niceNumber(input: { value: number; round: boolean }): number {
  if (input.value <= 0) {
    return 1;
  }

  const exponent = Math.floor(Math.log10(input.value));
  const magnitude = NICE_NUMBER_BASE ** exponent;
  const fraction = input.value / magnitude;
  const multiplier = selectNiceMultiplier({ fraction, round: input.round });

  return multiplier * magnitude;
}

function computeNiceTicks(input: {
  min: number;
  max: number;
  targetTickCount: number;
}): number[] {
  if (!Number.isFinite(input.min) || !Number.isFinite(input.max)) {
    return [0, 1];
  }

  let min = input.min;
  let max = input.max;
  if (min === max) {
    const spread = Math.abs(min) > 0
      ? Math.abs(min) * EQUAL_TICK_SPREAD_RATIO
      : EQUAL_DOMAIN_SPREAD_FALLBACK;
    min -= spread;
    max += spread;
  }
  const roughRange = Math.abs(max - min);
  const niceRange = niceNumber({ value: roughRange, round: false });
  const tickSpacing = niceNumber({
    value: niceRange / Math.max(1, input.targetTickCount - 1),
    round: true,
  });
  const niceMin = Math.floor(min / tickSpacing) * tickSpacing;
  const niceMax = Math.ceil(max / tickSpacing) * tickSpacing;
  const ticks: number[] = [];
  for (let value = niceMin; value <= niceMax + tickSpacing / 2; value += tickSpacing) {
    ticks.push(Number(value.toFixed(10)));
  }
  return ticks;
}

function getValueBounds(values: number[]): {
  valueMin: number;
  valueMax: number;
} {
  return {
    valueMin: Math.min(...values),
    valueMax: Math.max(...values),
  };
}

function getDomainPadding(input: {
  valueMin: number;
  valueMax: number;
}): number {
  const valueRange = input.valueMax - input.valueMin;

  if (valueRange === 0) {
    return Math.max(EQUAL_DOMAIN_SPREAD_FALLBACK, Math.abs(input.valueMax) * VALUE_DOMAIN_PADDING_RATIO);
  }

  return valueRange * VALUE_DOMAIN_PADDING_RATIO;
}

function getAutoDomain(input: {
  valueMin: number;
  valueMax: number;
  padding: number;
}): [number, number] {
  const autoMin = input.valueMin >= 0 ? 0 : input.valueMin - input.padding;
  return [autoMin, input.valueMax + input.padding];
}

function getExpandedEqualDomain(input: {
  min: number;
  max: number;
}): [number, number] {
  if (input.min !== input.max) {
    return [input.min, input.max];
  }

  const spread = Math.abs(input.min) > 0
    ? Math.abs(input.min) * VALUE_DOMAIN_PADDING_RATIO
    : EQUAL_DOMAIN_SPREAD_FALLBACK;

  return [input.min - spread, input.max + spread];
}

function normalizeDomainOrder(domain: [number, number]): [number, number] {
  return domain[0] < domain[1] ? domain : [domain[1], domain[0]];
}

function resolveValueDomain(input: {
  values: number[];
  explicitMin: number | null;
  explicitMax: number | null;
}): [number, number] | null {
  if (input.values.length === 0) {
    return null;
  }

  const valueBounds = getValueBounds(input.values);
  const padding = getDomainPadding(valueBounds);
  const autoDomain = getAutoDomain({ ...valueBounds, padding });
  const resolvedDomain = getExpandedEqualDomain({
    min: input.explicitMin ?? autoDomain[0],
    max: input.explicitMax ?? autoDomain[1],
  });

  return normalizeDomainOrder(resolvedDomain);
}

function buildSmoothLinePath(points: ReadonlyArray<XYChartPoint>, tension = 0.6): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }
  const segments = points.slice(0, -1).map((current, index) => {
    const previous = points[index - 1] ?? current;
    const next = points[index + 1];
    const afterNext = points[index + 2] ?? next;
    const c1 = {
      x: current.x + ((next.x - previous.x) * tension) / 6,
      y: current.y + ((next.y - previous.y) * tension) / 6,
    };
    const c2 = {
      x: next.x - ((afterNext.x - current.x) * tension) / 6,
      y: next.y - ((afterNext.y - current.y) * tension) / 6,
    };
    return `C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${next.x} ${next.y}`;
  });
  return `M ${points[0].x} ${points[0].y} ${segments.join(' ')}`;
}

function buildSmoothAreaPath(
  points: ReadonlyArray<XYChartPoint>,
  zeroPosition: number,
  horizontal: boolean
): string {
  if (points.length < 2) return '';
  const linePath = buildSmoothLinePath(points);
  const first = points[0];
  const last = points[points.length - 1];
  if (horizontal) {
    return `${linePath} L ${zeroPosition} ${last.y} L ${zeroPosition} ${first.y} Z`;
  }
  return `${linePath} L ${last.x} ${zeroPosition} L ${first.x} ${zeroPosition} Z`;
}

function createVerticalBarPath(input: {
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
  roundFromBottom: boolean;
}): string {
  if (input.width <= 0 || input.height <= 0) return '';
  const radius = Math.min(input.radius, input.width / 2, input.height / 2);
  if (!input.roundFromBottom) {
    return [
      `M ${input.x} ${input.y + input.height}`,
      `L ${input.x} ${input.y + radius}`,
      `Q ${input.x} ${input.y} ${input.x + radius} ${input.y}`,
      `L ${input.x + input.width - radius} ${input.y}`,
      `Q ${input.x + input.width} ${input.y} ${input.x + input.width} ${input.y + radius}`,
      `L ${input.x + input.width} ${input.y + input.height}`,
      'Z',
    ].join(' ');
  }
  return [
    `M ${input.x} ${input.y}`,
    `L ${input.x} ${input.y + input.height - radius}`,
    `Q ${input.x} ${input.y + input.height} ${input.x + radius} ${input.y + input.height}`,
    `L ${input.x + input.width - radius} ${input.y + input.height}`,
    `Q ${input.x + input.width} ${input.y + input.height} ${input.x + input.width} ${input.y + input.height - radius}`,
    `L ${input.x + input.width} ${input.y}`,
    'Z',
  ].join(' ');
}

function createHorizontalBarPath(input: {
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
  roundFromStart: boolean;
}): string {
  if (input.width <= 0 || input.height <= 0) return '';
  const radius = Math.min(input.radius, input.width / 2, input.height / 2);
  if (!input.roundFromStart) {
    return [
      `M ${input.x} ${input.y}`,
      `L ${input.x + input.width - radius} ${input.y}`,
      `Q ${input.x + input.width} ${input.y} ${input.x + input.width} ${input.y + radius}`,
      `L ${input.x + input.width} ${input.y + input.height - radius}`,
      `Q ${input.x + input.width} ${input.y + input.height} ${input.x + input.width - radius} ${input.y + input.height}`,
      `L ${input.x} ${input.y + input.height}`,
      'Z',
    ].join(' ');
  }
  return [
    `M ${input.x + radius} ${input.y}`,
    `L ${input.x + input.width} ${input.y}`,
    `L ${input.x + input.width} ${input.y + input.height}`,
    `L ${input.x + radius} ${input.y + input.height}`,
    `Q ${input.x} ${input.y + input.height} ${input.x} ${input.y + input.height - radius}`,
    `L ${input.x} ${input.y + radius}`,
    `Q ${input.x} ${input.y} ${input.x + radius} ${input.y}`,
    'Z',
  ].join(' ');
}

function truncateLabel(label: string, maxLength: number): string {
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength)}…`;
}

export function formatXYChartTick(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `${formatPlainNumber(value / 1_000_000, 1)}M`;
  }
  if (abs >= 1000) {
    return `${formatPlainNumber(value / 1000, 1)}k`;
  }
  return formatPlainNumber(value, 2);
}

const REGEX_FIRST_CAPTURE_REF = '$1';

function trimTrailingZeros(value: string): string {
  return value.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, REGEX_FIRST_CAPTURE_REF);
}

function formatPlainNumber(value: number, maxFractionDigits = 2): string {
  if (Number.isInteger(value)) return String(value);
  return trimTrailingZeros(value.toFixed(maxFractionDigits));
}

export interface XYChartLayoutInput {
  parsed: XYChartParsed;
  containerWidth: number;
  containerHeight: number;
  zoom: number;
}

interface XYChartPlotGeometry {
  width: number;
  height: number;
  margins: XYChartMargins;
  plotWidth: number;
  plotHeight: number;
  horizontal: boolean;
}

interface XYChartValueScale {
  valueDomain: [number, number];
  valueRange: [number, number];
  zeroPosition: number;
  ticks: XYChartTickLayout[];
}

interface XYChartCategoryScale {
  categoryCount: number;
  categoryStep: number;
  categoryCenters: number[];
  categoryLabels: XYChartCategoryLabel[];
  rotateCategoryLabels: boolean;
}

interface XYChartBarMetrics {
  barOrderBySeriesIndex: Record<number, number>;
  barGroupSize: number;
  singleBarSize: number;
}

interface XYChartSeriesBuildContext {
  parsed: XYChartParsed;
  geometry: XYChartPlotGeometry;
  valueScale: XYChartValueScale;
  categoryScale: XYChartCategoryScale;
  barMetrics: XYChartBarMetrics;
}

interface BuildSeriesLayoutInput {
  dataSeries: XYChartDataSeries;
  seriesIndex: number;
  context: XYChartSeriesBuildContext;
}

interface BuildBarDatumInput {
  value: number;
  categoryIndex: number;
  center: number;
  barOrder: number;
  context: XYChartSeriesBuildContext;
}

function resolvePlotGeometry(input: XYChartLayoutInput): XYChartPlotGeometry | null {
  const { parsed, zoom } = input;
  const margins = parsed.horizontal ? XYCHART_HORIZONTAL_MARGINS : XYCHART_VERTICAL_MARGINS;

  const naturalWidth = Math.max(input.containerWidth, XYCHART_MIN_WIDTH);
  const naturalHeight = Math.max(input.containerHeight, XYCHART_MIN_HEIGHT);

  // Apply zoom along the category axis. Vertical layouts grow horizontally,
  // horizontal layouts grow vertically. The chart becomes scrollable on that
  // axis once it exceeds the container.
  const width = parsed.horizontal ? naturalWidth : Math.round(naturalWidth * zoom);
  const height = parsed.horizontal ? Math.round(naturalHeight * zoom) : naturalHeight;

  const plotWidth = width - margins.left - margins.right;
  const plotHeight = height - margins.top - margins.bottom;
  if (plotWidth <= 0 || plotHeight <= 0) return null;

  return {
    width,
    height,
    margins,
    plotWidth,
    plotHeight,
    horizontal: parsed.horizontal,
  };
}

function buildValueScale(input: {
  parsed: XYChartParsed;
  geometry: XYChartPlotGeometry;
}): XYChartValueScale | null {
  const { parsed, geometry } = input;
  const { plotWidth, plotHeight } = geometry;
  const categoryCount = parsed.xAxis.categories.length;
  if (categoryCount === 0) return null;

  const allValues = parsed.series.flatMap((series) => series.data);
  const valueDomainCandidate = resolveValueDomain({
    values: allValues,
    explicitMin: parsed.yAxis.min,
    explicitMax: parsed.yAxis.max,
  });
  if (!valueDomainCandidate) return null;

  const niceTicks = computeNiceTicks({
    min: valueDomainCandidate[0],
    max: valueDomainCandidate[1],
    targetTickCount: TARGET_TICK_COUNT,
  });

  const valueDomain: [number, number] =
    niceTicks.length > 1
      ? [niceTicks[0], niceTicks[niceTicks.length - 1]]
      : valueDomainCandidate;

  // The value-axis maps onto the perpendicular axis of the plot. Vertical
  // layouts grow upward (so domain[1] sits at the top, position 0).
  const valueRange: [number, number] = parsed.horizontal ? [0, plotWidth] : [plotHeight, 0];

  const rawZeroPosition = scaleLinear(0, valueDomain, valueRange);
  const zeroPosition = parsed.horizontal
    ? clamp(rawZeroPosition, 0, plotWidth)
    : clamp(rawZeroPosition, 0, plotHeight);

  const ticks: XYChartTickLayout[] = niceTicks.map((tick) => ({
    value: tick,
    position: scaleLinear(tick, valueDomain, valueRange),
    label: formatXYChartTick(tick),
  }));

  return {
    valueDomain,
    valueRange,
    zeroPosition,
    ticks,
  };
}

function buildCategoryScale(input: {
  parsed: XYChartParsed;
  geometry: XYChartPlotGeometry;
}): XYChartCategoryScale | null {
  const { parsed, geometry } = input;
  const { plotWidth, plotHeight } = geometry;
  const categoryCount = parsed.xAxis.categories.length;
  if (categoryCount === 0) return null;

  const categorySpan = parsed.horizontal ? plotHeight : plotWidth;
  const categoryStep = categorySpan / categoryCount;

  const categoryCenters = Array.from(
    { length: categoryCount },
    (_, index) => categoryStep * index + categoryStep / 2
  );

  const categoryLabels: XYChartCategoryLabel[] = parsed.xAxis.categories.map((category, index) => ({
    text: truncateLabel(
      category,
      parsed.horizontal ? HORIZONTAL_CATEGORY_LABEL_MAX_LENGTH : VERTICAL_CATEGORY_LABEL_MAX_LENGTH
    ),
    fullText: category,
    position: categoryCenters[index],
  }));

  return {
    categoryCount,
    categoryStep,
    categoryCenters,
    categoryLabels,
    rotateCategoryLabels:
      !parsed.horizontal &&
      categoryCount > ROTATED_CATEGORY_LABEL_MIN_COUNT &&
      categoryStep < ROTATED_CATEGORY_LABEL_STEP_LIMIT,
  };
}

function buildBarMetrics(input: {
  series: XYChartDataSeries[];
  categoryStep: number;
}): XYChartBarMetrics {
  const barSeriesIndices = input.series.flatMap((series, seriesIndex) =>
    series.type === XYCHART_SERIES_TYPE.Bar ? [seriesIndex] : []
  );
  const barOrderBySeriesIndex = Object.fromEntries(
    barSeriesIndices.map((seriesIndex, order) => [seriesIndex, order])
  ) as Record<number, number>;

  const barCount = barSeriesIndices.length;
  const barGroupSize = input.categoryStep * XYCHART_BAR_GROUP_RATIO;
  const singleBarSize =
    barCount > 0
      ? Math.max(1, (barGroupSize - XYCHART_BAR_GAP * Math.max(0, barCount - 1)) / barCount)
      : 0;

  return { barOrderBySeriesIndex, barGroupSize, singleBarSize };
}

function buildSeriesLayouts(context: XYChartSeriesBuildContext): XYChartSeriesLayout[] {
  return context.parsed.series.map((dataSeries, seriesIndex) =>
    buildSeriesLayout({ dataSeries, seriesIndex, context })
  );
}

function buildSeriesLayout({
  dataSeries,
  seriesIndex,
  context,
}: BuildSeriesLayoutInput): XYChartSeriesLayout {
  const seriesColor = XYCHART_SERIES_COLORS[dataSeries.colorIndex % XYCHART_SERIES_COLORS.length];

  if (dataSeries.type === XYCHART_SERIES_TYPE.Bar) {
    return buildBarSeriesLayout({ dataSeries, seriesIndex, context, seriesColor });
  }

  return buildLineSeriesLayout({ dataSeries, seriesIndex, context, seriesColor });
}

function buildBarSeriesLayout(input: BuildSeriesLayoutInput & {
  seriesColor: string;
}): XYChartSeriesLayout {
  const { dataSeries, seriesIndex, context, seriesColor } = input;
  const barOrder = context.barMetrics.barOrderBySeriesIndex[seriesIndex] ?? 0;
  const bars: XYChartBarDatum[] = dataSeries.data.map((value, categoryIndex) =>
    buildBarDatum({
      value,
      categoryIndex,
      center: context.categoryScale.categoryCenters[categoryIndex],
      barOrder,
      context,
    })
  );

  return {
    seriesIndex,
    type: XYCHART_SERIES_TYPE.Bar,
    color: seriesColor,
    label: dataSeries.label,
    bars,
    linePoints: [],
    linePath: '',
    areaPath: '',
  };
}

function buildBarDatum(input: BuildBarDatumInput): XYChartBarDatum {
  if (input.context.geometry.horizontal) {
    return buildHorizontalBarDatum(input);
  }

  return buildVerticalBarDatum(input);
}

function buildVerticalBarDatum(input: BuildBarDatumInput): XYChartBarDatum {
  const { categoryIndex, value, center, barOrder, context } = input;
  const { plotHeight } = context.geometry;
  const { valueDomain, zeroPosition } = context.valueScale;
  const { barGroupSize, singleBarSize } = context.barMetrics;
  const groupStartX = center - barGroupSize / 2;
  const barX = groupStartX + barOrder * (singleBarSize + XYCHART_BAR_GAP);
  const valueY = scaleLinear(value, valueDomain, [plotHeight, 0]);
  const barY = Math.min(zeroPosition, valueY);
  const barHeight = Math.abs(zeroPosition - valueY);

  return {
    categoryIndex,
    value,
    path: createVerticalBarPath({
      x: barX,
      y: barY,
      width: singleBarSize,
      height: barHeight,
      radius: XYCHART_BAR_RADIUS,
      roundFromBottom: value < 0,
    }),
    centerX: barX + singleBarSize / 2,
    centerY: valueY,
    zeroX: barX + singleBarSize / 2,
    zeroY: zeroPosition,
  };
}

function buildHorizontalBarDatum(input: BuildBarDatumInput): XYChartBarDatum {
  const { categoryIndex, value, center, barOrder, context } = input;
  const { plotWidth } = context.geometry;
  const { valueDomain, zeroPosition } = context.valueScale;
  const { barGroupSize, singleBarSize } = context.barMetrics;
  const groupStartY = center - barGroupSize / 2;
  const barY = groupStartY + barOrder * (singleBarSize + XYCHART_BAR_GAP);
  const valueX = scaleLinear(value, valueDomain, [0, plotWidth]);
  const barX = Math.min(zeroPosition, valueX);
  const barWidth = Math.abs(zeroPosition - valueX);

  return {
    categoryIndex,
    value,
    path: createHorizontalBarPath({
      x: barX,
      y: barY,
      width: barWidth,
      height: singleBarSize,
      radius: XYCHART_BAR_RADIUS,
      roundFromStart: value < 0,
    }),
    centerX: valueX,
    centerY: barY + singleBarSize / 2,
    zeroX: zeroPosition,
    zeroY: barY + singleBarSize / 2,
  };
}

function buildLineSeriesLayout(input: BuildSeriesLayoutInput & {
  seriesColor: string;
}): XYChartSeriesLayout {
  const { dataSeries, seriesIndex, context, seriesColor } = input;
  const linePoints = dataSeries.data.map((value, categoryIndex) =>
    buildLinePoint({ value, categoryIndex, context })
  );

  return {
    seriesIndex,
    type: XYCHART_SERIES_TYPE.Line,
    color: seriesColor,
    label: dataSeries.label,
    bars: [],
    linePoints,
    linePath: buildSmoothLinePath(linePoints),
    areaPath: buildSmoothAreaPath(
      linePoints,
      context.valueScale.zeroPosition,
      context.parsed.horizontal
    ),
  };
}

function buildLinePoint(input: {
  value: number;
  categoryIndex: number;
  context: XYChartSeriesBuildContext;
}): XYChartLinePoint {
  const { value, categoryIndex, context } = input;
  const center = context.categoryScale.categoryCenters[categoryIndex];
  const { valueDomain } = context.valueScale;
  const { plotWidth, plotHeight, horizontal } = context.geometry;

  if (!horizontal) {
    return {
      categoryIndex,
      value,
      x: center,
      y: scaleLinear(value, valueDomain, [plotHeight, 0]),
    };
  }

  return {
    categoryIndex,
    value,
    x: scaleLinear(value, valueDomain, [0, plotWidth]),
    y: center,
  };
}

export function buildXYChartLayout(input: XYChartLayoutInput): XYChartLayout | null {
  const { parsed } = input;
  const geometry = resolvePlotGeometry(input);
  if (!geometry) return null;

  const categoryScale = buildCategoryScale({ parsed, geometry });
  if (!categoryScale) return null;

  const valueScale = buildValueScale({ parsed, geometry });
  if (!valueScale) return null;

  const barMetrics = buildBarMetrics({
    series: parsed.series,
    categoryStep: categoryScale.categoryStep,
  });
  const series = buildSeriesLayouts({
    parsed,
    geometry,
    valueScale,
    categoryScale,
    barMetrics,
  });

  return {
    width: geometry.width,
    height: geometry.height,
    margins: geometry.margins,
    plotWidth: geometry.plotWidth,
    plotHeight: geometry.plotHeight,
    horizontal: geometry.horizontal,
    categories: categoryScale.categoryLabels,
    rotateCategoryLabels: categoryScale.rotateCategoryLabels,
    ticks: valueScale.ticks,
    valueDomain: valueScale.valueDomain,
    zeroPosition: valueScale.zeroPosition,
    series,
    xAxisTitle: parsed.xAxis.title,
    yAxisTitle: parsed.yAxis.title,
    categoryCenters: categoryScale.categoryCenters,
  };
}
